import { Component, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * Error Boundary component to catch JavaScript errors in child components.
 * Prevents the entire app from crashing when a component fails.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });
    
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // If custom fallback provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 bg-gray-50">
          <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-danger" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {this.props.componentName 
                ? `Error in ${this.props.componentName}` 
                : 'Something went wrong'}
            </h2>
            
            <p className="text-gray-600 mb-4">
              An unexpected error occurred. Please try again or reload the page.
            </p>

            {/* Error details (collapsed by default) */}
            {this.state.error && (
              <details className="text-left mb-4">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Technical details
                </summary>
                <div className="mt-2 p-3 bg-gray-100 rounded-lg overflow-auto max-h-40">
                  <code className="text-xs text-danger break-all">
                    {this.state.error.message}
                  </code>
                  {this.state.errorInfo && (
                    <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Compact error fallback for smaller components like sidebars
 */
export function CompactErrorFallback({ 
  message = 'Failed to load', 
  onRetry 
}: { 
  message?: string; 
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center p-4 text-center">
      <AlertTriangle className="w-6 h-6 text-danger mb-2" />
      <p className="text-sm text-gray-600 mb-2">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-sm text-primary hover:underline"
        >
          Try again
        </button>
      )}
    </div>
  );
}

/**
 * Map-specific error fallback
 */
export function MapErrorFallback() {
  return (
    <div className="h-full flex items-center justify-center bg-gray-100">
      <div className="text-center p-6">
        <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-3" />
        <h3 className="font-medium text-gray-900 mb-1">Map failed to load</h3>
        <p className="text-sm text-gray-600 mb-3">
          Check your internet connection for map tiles
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary hover:underline"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

/**
 * Photo viewer error fallback
 */
export function PhotoErrorFallback() {
  return (
    <div className="h-full flex items-center justify-center bg-gray-900">
      <div className="text-center p-6">
        <AlertTriangle className="w-12 h-12 text-warning mx-auto mb-3" />
        <h3 className="font-medium text-white mb-1">Photo viewer error</h3>
        <p className="text-sm text-gray-400 mb-3">
          Failed to render photo viewer
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary hover:underline"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
